import { BaseResponse } from '@aifs-shared/common/base-response';
import { BaseService } from '@aifs-shared/common/base-service';
import { ResponseData } from '@aifs-shared/common/response-data';
import { User } from '@aifs-shared/user/user';
import { UserService } from '@aifs-shared/user/user.service';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { Observable, Subject } from 'rxjs';
import { catchError, retry } from 'rxjs/operators';

import { Bookmark } from './bookmark';
@Injectable({
    providedIn: 'root'
})
export class BookmarkService extends BaseService {

    constructor(
        private http: HttpClient,
        private userService: UserService) {
        super();
        this.userService.userChanged.subscribe(
            (user: User | undefined) => {
                if (user && !(this.currentUser) || user && user.id !== this.currentUser?.id) {
                    this.currentUser = user;

                    this.reloadForCache(user);

                } else if (!user) {
                    //console.debug(`User has signed out`);
                    this.currentUser = undefined;
                    localStorage.removeItem(`bookmarks`);
                }
            });
    }

    public reloadForCache(user: User) {
        this.getBookmarks(user.id)
            .subscribe((r: Bookmark[]) => {
                // Only cache if caching is not disabled
                if (!environment.cacheDisabled) {
                    localStorage.setItem(`bookmarks`, JSON.stringify(r));
                }
            }, error => {
                console.error(error);
            });
    }

    public getCachedBookmarks(): Bookmark[] | undefined {
        // If caching is disabled, always return undefined
        if (environment.cacheDisabled) {
            return undefined;
        }

        const bookmarks = localStorage.getItem(`bookmarks`);
        if (bookmarks) {
            return JSON.parse(bookmarks) as Bookmark[];
        }
        return undefined;
    }

    public getBookmarks(userId: number): Observable<Bookmark[]> {
        const s = new Subject<Bookmark[]>();

        this
            .http
            .get<BaseResponse>(environment.ServiceUrl_Agency_Bookmarks_Get(userId))
            .pipe(
                retry(3), // retry a failed request up to 3 times
                catchError(this.handleError)
            )
            .subscribe({
                next: (data: BaseResponse) => {
                    const response = this.getResultData<GetBookmarkResponse>(data);
                    // Only cache if caching is not disabled
                    if (!environment.cacheDisabled) {
                        localStorage.setItem(`bookmarks`, JSON.stringify(response.bookmarks));
                    }
                    s.next(response.bookmarks);
                },
                error: (error: any) => {
                    console.error(error);
                    s.error(error);
                }
            });

        return s;
    }

    public saveBookmark(userId: number, bookmarkRoleId: number, bookmarkRoleType: string): Observable<boolean> {
        const s = new Subject<boolean>();

        this
            .http
            .post<BaseResponse>(environment.ServiceUrl_Agency_Bookmarks_Post(userId),
                {
                    roleId: bookmarkRoleId,
                    roleType: bookmarkRoleType
                }
            )
            .pipe(
                catchError(this.handleError)
            )
            .subscribe(
                data => {
                    const response = this.getResultData<boolean>(data);
                    s.next(response);
                },
                error => {
                    s.error(error);
                });

        return s;
    }

    public deleteBookmark(userId: number, bookmarkId: number): Observable<boolean> {
        const s = new Subject<boolean>();

        this
            .http
            .post<BaseResponse>(environment.ServiceUrl_Agency_Bookmarks_Delete(userId),
                {
                    bookmarkId: bookmarkId,
                    userId: userId
                }
            )
            .pipe(
                catchError(this.handleError)
            )
            .subscribe(
                data => {
                    const response = this.getResultData<boolean>(data);
                    // Only clear cache if caching is not disabled
                    if (!environment.cacheDisabled) {
                        localStorage.removeItem(`bookmarks`);
                    }
                    s.next(response);
                },
                error => {
                    s.error(error);
                });

        return s;
    }

    currentUser?: User | null;
}

export class GetBookmarkResponse implements ResponseData { bookmarks: Bookmark[] = [] }
